// Bài hướng dẫn trang Ca lễ tân — /letan-shifts.
//
// Bài chia theo BA thẻ của màn: người khai ca đọc thẻ đầu, người chốt lịch đọc
// hai thẻ sau. Nhãn thẻ lấy từ từ điển nhãn của màn, không gõ chuỗi lần hai —
// xem `../letan-shift-guide-tabs.ts`.
//
// ⚠️ Ranh giới thông tin ở `../types.ts` áp NGUYÊN VẸN, và trang này đứng sát
// vạch hơn phần lớn trang khác vì thẻ cuối bày giờ công cùng đơn giá. Bài của
// thẻ đó cố ý CHỈ nói cách đọc bảng và cách sửa một dòng, không nói tiền ra thế
// nào — chỗ nới duy nhất của kho là trang Cơ chế lương.

import type { GuideBook } from "../types";
import { LETAN_SHIFT_TAB_LABEL } from "@/features/letan/labels";

export const LETAN_SHIFT_GUIDES: GuideBook = {
  letan_shifts: {
    purpose: "Nơi lễ tân khai ca trực theo tuần và theo dõi ca đã được nhận.",
    where: [
      'Đầu trang: nút "+ Đăng ký", mũi tên đổi tuần và nút "Tuần này".',
      "Số thẻ bạn thấy tuỳ theo công việc của bạn: người chỉ khai ca không thấy hàng thẻ nào, người chốt lịch thấy thêm hàng thẻ ngay trên lưới.",
      'Dòng chữ nhỏ dưới tên trang nhắc cách khai ca: bấm "+ Đăng ký" hoặc ô "+ Ca …" ở hàng dưới cùng của lịch.',
    ],
    howItWorks: [
      "Mọi thứ trên trang xoay quanh MỘT tuần: đổi tuần là đổi toàn bộ danh sách bên dưới.",
      "Khai ca xong ca chưa thành lịch chính thức — còn một chặng nữa mới thành.",
      "Tuần đã chốt thì cả trang thành chỉ đọc, kể cả ca của chính mình.",
    ],
    statuses: [
      { label: "Chờ duyệt", meaning: "Đã khai, đang đợi người chốt lịch xem." },
      { label: "Đã duyệt", meaning: "Ca chính thức, cứ theo đó mà đi trực." },
      { label: "Bị từ chối", meaning: "Không nhận ca này; lý do ghi ngay trên dòng." },
      { label: "Đã huỷ", meaning: "Ca đã bỏ, kèm lý do huỷ." },
      { label: "Đã khoá", meaning: "Tuần đã chốt, không khai và không sửa được nữa." },
    ],
    tabs: [
      {
        key: "register",
        label: LETAN_SHIFT_TAB_LABEL.register,
        purpose: "Lưới cả tuần của một cơ sở, và chỗ khai ca bạn nhận trực.",
        where: [
          'Nút "+ Đăng ký" ở đầu trang, cạnh nút đổi tuần và nút "Tuần này".',
          "Hàng nút chọn cơ sở ngay trên lưới — mỗi lần xem một cơ sở, chỉ có những cơ sở bạn được phân công; cuối hàng đó là nút thu nhỏ / phóng to lưới.",
          "Lưới bảy cột là bảy ngày, trục dọc là giờ trong ngày. Cột hôm nay tô đậm và ghi chữ \"Hôm nay\"; ngày đã trôi qua thì mờ đi.",
          "Dưới số ngày ở đầu mỗi cột là số ca của ngày đó.",
          "Hàng nút \"+ ...\" dưới đáy lưới để khai thẳng một ca, khỏi mở hộp.",
          "Mỗi ca là một thẻ dài đúng bằng giờ trực, ghi tên người trực kèm thẻ màu tình trạng.",
          'Tuần đang chốt thì đầu trang hiện thẻ đỏ "Đã khoá" và dòng "Lý do khoá:" nếu người chốt có ghi.',
          "Trong hộp khai ca: ngày trực, loại ca, giờ bắt đầu, giờ kết thúc và cơ sở.",
          'Khung "Cảnh báo nhân sự" ngay dưới hàng thẻ liệt kê từng khoảng giờ quầy đang thiếu người: tên cơ sở, ngày, từ giờ – tới giờ, có bao nhiêu người và cần bao nhiêu.',
        ],
        howItWorks: [
          "Trang mở sẵn ở TUẦN SAU chứ không phải tuần này, vì việc thường ngày ở đây là khai trước cho tuần tới.",
          "Trang mở sẵn ở cơ sở bạn xem lần trước trên máy này; lần đầu thì mở ở cơ sở chính của bạn. Máy nhớ riêng cho từng người, nên máy quầy dùng chung cũng không lẫn.",
          "Ca dài vẽ thành thẻ cao, ca ngắn thẻ thấp — nhìn chiều cao là biết ai trực bao lâu mà không phải đọc giờ.",
          "Nhiều người cùng một khung giờ thì các thẻ đứng cạnh nhau, và ngày đó tự nong rộng ra để thẻ nào cũng đọc được tên; lưới rộng quá màn thì kéo ngang, hoặc bấm nút thu nhỏ để xem trọn tuần.",
          "Mỗi loại ca có khung giờ mặc định riêng, và hai miền có bộ ca khác nhau; chọn ca là giờ tự điền theo.",
          "Cần trực lệch giờ mặc định thì sửa giờ ngay trong hộp, dòng đó sẽ ghi rõ là đã đổi.",
          "Ca vừa khai nằm ở chặng chờ; phải qua chặng sau mới thành lịch chính thức.",
          "Tới ngày trực, bạn vào ca ở ô Check-in trang Tổng quan đúng cơ sở, trong giờ ca, thì ca còn chờ tự thành lịch chính thức — không phải đợi ai bấm (áp dụng khi bản mới lên web).",
          "Cảnh báo thiếu người tính theo giờ quầy của cơ sở, đếm cả ca đã duyệt lẫn ca đang chờ: dòng đỏ là thiếu trong hôm nay tới 3 ngày tới, dòng vàng là cuối tuần còn thiếu, dòng mờ là ngày xa hơn. Ngày đã qua không báo.",
          "Chỉ dời được ca trong cùng tuần; muốn sang tuần khác thì huỷ ca rồi khai lại ở tuần đó.",
          "Huỷ ca bắt buộc ghi lý do, để tuần sau còn biết vì sao ca đó trống.",
          "Khoá tính theo từng cơ sở: cơ sở này chốt rồi không có nghĩa cơ sở kia cũng chốt.",
          "Mỗi ca chỉ nhận tối đa số người mà cơ sở đó khai; ca đủ người thì nút khai ca của ca ấy thôi hiện. Số này khác nhau giữa các cơ sở nên cùng một ca có nơi còn chỗ, nơi hết.",
          "Một người không khai được hai ca trùng giờ trong cùng ngày, kể cả ở hai cơ sở khác nhau; hai ca nối tiếp nhau (ca này hết đúng lúc ca kia vào) thì được.",
        ],
        statuses: [
          { label: "Chờ duyệt", meaning: "Đã khai, đang đợi người chốt lịch xem." },
          { label: "Đã duyệt", meaning: "Ca chính thức, cứ theo đó mà đi trực." },
          { label: "Bị từ chối", meaning: "Không nhận ca này; lý do ghi ngay trên dòng." },
          { label: "Đã huỷ", meaning: "Ca đã bỏ, kèm lý do huỷ." },
          { label: "Đã khoá", meaning: "Tuần của cơ sở này đã chốt, không khai và không sửa được nữa." },
          { label: "Chờ duyệt nghỉ", meaning: "Bạn đã xin nghỉ ca đã duyệt; ca vẫn là của bạn cho tới khi quản lý trực tiếp duyệt." },
          { label: "Sát giờ", meaning: "Nhãn đỏ: đơn xin nghỉ gửi khi còn chưa tới 24 giờ là vào ca." },
        ],
        blocks: [
          {
            for: ["reception"],
            title: "Khai ca trực của bạn",
            steps: [
              "Kiểm tra bạn đang đứng đúng tuần và đúng cơ sở mình sẽ tới.",
              'Bấm ô trống ngay trên lưới ở ngày và ca bạn nhận, hoặc bấm "+ Đăng ký" rồi chọn tay.',
              "Giữ nguyên giờ mặc định nếu trực đúng khung; trực lệch giờ thì sửa hai ô giờ.",
              "Lưu xong theo dõi tới khi dòng của bạn chuyển sang đã duyệt.",
              "Tới ngày trực, vào ca ở ô Check-in trang Tổng quan đúng cơ sở trong giờ ca: ca còn chờ sẽ tự chuyển sang đã duyệt (áp dụng khi bản mới lên web).",
              'Ca đã duyệt mà bận đột xuất: bấm vào ca của bạn trên lưới, bấm "Xin nghỉ ca này", ghi lý do; đã nhờ được ai thay thì chọn ở ô "Người thay", rồi bấm "Gửi đơn". Gửi càng sớm càng tốt.',
              'Đổi ca: xin nghỉ ca cũ như trên, rồi khai ca mới như thường — ca mới cũng chờ duyệt như mọi ca.',
              'Đổi ý không nghỉ nữa: mở lại ca đó, bấm "Rút lại đơn" khi quản lý chưa quyết.',
            ],
            troubles: [
              "Không bấm được gì trên lưới: tuần của cơ sở đó đã chốt — cần đổi thì hỏi quản lý trực tiếp.",
              "Ca bị từ chối: đọc lý do ghi trên dòng rồi khai lại cho đúng.",
              "Muốn dời sang tuần khác: huỷ ca này rồi khai ca mới ở tuần kia, không kéo ngang được.",
              'Khai xong mà lưới không thấy ca: ca nằm ở tuần của ngày bạn chọn, không phải tuần đang mở — bấm mũi tên đổi tuần tới đúng tuần đó. Báo "Bạn đã đăng ký ca này rồi" cũng nghĩa là ca đã có ở tuần đó.',
              "Báo giờ bắt đầu phải trước giờ kết thúc: xem lại hai ô giờ.",
              "Hàng nút không có cơ sở bạn cần: bạn chưa được phân công ở cơ sở đó — hỏi quản lý trực tiếp.",
              'Báo "đã có … chồng giờ với khung …": hôm đó bạn đã có một ca khác trùng giờ (có thể ở cơ sở khác) — sửa giờ để hai ca nối tiếp nhau, hoặc xoá / xin nghỉ ca kia trước.',
              "Lưới trống trơn: xem lại bạn có đang chọn đúng cơ sở không, lưới chỉ hiện một cơ sở mỗi lần.",
              "Một ca không có nút khai: ca đó đã đủ người theo số cơ sở đang khai — chọn ca khác, hoặc hỏi quản lý trực tiếp nếu vẫn cần nhận.",
              'Không thấy nút "Xin nghỉ ca này": chỉ ca ĐÃ DUYỆT của chính bạn mới có; ca đang chờ duyệt thì bấm "Xoá" là xong.',
              'Hộp báo đỏ "chưa tới 24 giờ là vào ca": đơn vẫn gửi được nhưng mang nhãn "Sát giờ" — gọi điện báo quản lý trực tiếp luôn.',
              'Máy báo "Ca đã bắt đầu hoặc đã qua": ca đó không xin nghỉ trong máy được nữa, báo thẳng quản lý trực tiếp.',
              "Đơn bị từ chối: lý do hiện ngay trong hộp ca; ca vẫn là của bạn, vẫn phải đi trực.",
            ],
          },
          {
            for: ["lead", "exec", "qc"],
            title: "Nhìn tuần trước khi chốt",
            steps: [
              'Mở tuần sắp tới và đọc khung "Cảnh báo nhân sự": dòng đỏ xử trước (thiếu người trong vài ngày tới), rồi tới dòng vàng (cuối tuần).',
              "Nhìn lưới xem ca nào còn trống, ca nào mới chỉ có người khai mà chưa được nhận.",
              "Thiếu người thì nhắc khai thêm trước khi chốt tuần.",
            ],
            troubles: [
              "Tuần trông thiếu người: xem đó là ca CHỜ DUYỆT hay chưa ai khai — hai chuyện khác nhau, chờ duyệt thì chỉ cần xử nốt bên thẻ Duyệt.",
              "Chốt tuần ở một cơ sở mà cơ sở khác vẫn khai được: đúng như vậy, khoá tính theo từng cơ sở.",
            ],
          },
        ],
      },
      {
        key: "approve",
        label: LETAN_SHIFT_TAB_LABEL.approve,
        purpose: "Danh sách ca đang chờ được nhận, và chỗ chốt tuần khi đã đủ người.",
        where: [
          "Số trong ngoặc trên tên thẻ là số ca đang chờ của tuần và cơ sở đang xem.",
          'Nút "Duyệt tất cả" ở đầu danh sách, kèm số ca sẽ được xử một lượt.',
          "Mỗi dòng: tên người khai, ngày, loại ca kèm khung giờ, cơ sở và thẻ tình trạng.",
          "Bấm vào một dòng để mở hộp xem chi tiết và quyết từng ca.",
          'Ở đầu trang: nút "Khoá tuần" kèm tên cơ sở, đổi thành "Mở khoá" khi tuần đang chốt.',
        ],
        howItWorks: [
          "Danh sách này chỉ gồm ca của TUẦN và CƠ SỞ đang xem; đổi cơ sở bên thẻ đầu là đổi luôn danh sách ở đây.",
          "Nhận cả loạt cũng chỉ chạm đúng phạm vi đó, không đụng tuần khác hay cơ sở khác.",
          "Xử xong một loạt thì có báo lại bao nhiêu ca được nhận, bao nhiêu ca bỏ qua — ca bỏ qua là ca đã được quyết từ trước, hoặc ca cần xem riêng (trùng giờ với ca đã nhận của cùng người, số giờ hay đơn giá khác thường); lý do hiện ngay dưới thông báo.",
          "Số giờ của một ca được so với độ dài CHUẨN của loại ca đó (Ca 1 là 8 giờ…), không so với giờ người khai tự chọn: khai lệch giờ vào ra mà tổng giờ vẫn vậy thì qua êm, khai dài hơn hẳn loại ca thì phải mở ca ra xem riêng.",
          "Có ca mới khai, người chốt lịch của cơ sở nhận chuông + Telegram ghi tên người, ca, giờ và cơ sở — không phải đợi tới lúc tự mở trang.",
          "Không nhận một ca thì bắt buộc ghi lý do, vì người khai đọc đúng dòng đó để biết đường sửa.",
          "Lễ tân xin nghỉ ca đã duyệt thì ca VẪN tính là của họ cho tới khi bạn duyệt; duyệt xong máy huỷ ca bằng đúng đường huỷ ca, lý do ghi \"Lễ tân xin nghỉ: …\". Có đơn mới bạn nhận chuông + Telegram.",
          "Chốt tuần cũng theo từng cơ sở: chốt xong cả người khai lẫn người quyết đều dừng tay ở cơ sở đó.",
          "Ca còn chờ mà tuần đã chốt thì ca đó không thành lịch chính thức — phải mở lại, xử nốt, rồi chốt lần nữa.",
          "Ca mà lễ tân đã vào ca đúng cơ sở trong giờ ca thì tự chuyển sang đã duyệt; danh sách chờ chỉ còn ca chưa ai vào ca và ca cần bạn xem kỹ. Ca tự duyệt vẫn huỷ được như ca bạn nhận (áp dụng khi bản mới lên web).",
          "Ca sắp tới ngày trực hoặc đã qua, và đơn xin nghỉ, nằm chờ quá một ngày thì chuông nhắc bạn; để thêm hai ngày nữa thì chuông báo lên quản lý cấp cao (áp dụng khi bản mới lên web).",
          'Ca đã duyệt của ngày đã qua mà người đó quên chấm công: mở ca, bấm "Xác nhận có đi làm" — tên bạn và lúc bấm được ghi lại, ca rời khung cảnh báo bên bảng lương. Không đứng ca thật thì huỷ ca thay vì xác nhận.',
        ],
        statuses: [
          { label: "Chờ duyệt", meaning: "Ca đang đợi được quyết." },
          { label: "Đã duyệt", meaning: "Ca đã thành lịch chính thức." },
          { label: "Bị từ chối", meaning: "Ca không được nhận; lý do hiện trên dòng cho người khai đọc." },
          { label: "Đã huỷ", meaning: "Ca từng được nhận rồi bỏ, kèm lý do." },
          { label: "Đã khoá", meaning: "Tuần của cơ sở này đã chốt." },
          { label: "Sát giờ", meaning: "Đơn xin nghỉ gửi khi còn chưa tới 24 giờ là vào ca — xử trước." },
        ],
        blocks: [
          {
            for: ["lead", "exec", "qc"],
            title: "Chốt lịch trực của tuần",
            steps: [
              'Trên cùng là "Đơn xin nghỉ chờ duyệt": đọc lý do và người thay (nếu có). Bấm "Duyệt nghỉ" thì ca đó chuyển sang Đã huỷ và rời khỏi giờ công; bấm "Từ chối" phải ghi lý do, ca giữ nguyên.',
              "Đơn có nhãn đỏ \"Sát giờ\" thì xử trước, rồi tìm người lấp ca.",
              "Đầu tuần mở thẻ này và rà danh sách đang chờ.",
              "Bấm từng dòng để xem chi tiết, hoặc nhận cả loạt khi đã xem qua và thấy ổn.",
              'Bấm "Duyệt" mà hiện khung đỏ "Vượt mức thường": đọc kỹ lý do. Sai thì bấm "Sửa lại số"; đúng là vậy thì bấm "Vẫn duyệt" — lượt đó được ghi lại kèm tên bạn.',
              'Sửa giờ một ca đã duyệt rồi bấm "Lưu ca" cũng đi qua đúng phép soát đó: hiện khung đỏ "Vượt mức thường" thì sai bấm "Sửa lại giờ", đúng bấm "Vẫn lưu" — lượt đó ghi lại số giờ cũ, số giờ mới và tên bạn.',
              "Không nhận một ca thì ghi rõ lý do để người khai biết đường sửa.",
              "Rà lại lưới bên thẻ đầu xem còn ngày nào trống không.",
              "Đủ người rồi thì chốt tuần cho cơ sở đó.",
            ],
            troubles: [
              "Còn ca chờ mà đã chốt tuần: mở lại, xử nốt rồi chốt lần nữa — ca còn chờ không thành lịch chính thức.",
              "Nhận cả loạt xong vẫn còn dòng: những ca đó đã được quyết từ trước nên bị bỏ qua, hoặc chúng thuộc cơ sở khác.",
              "Danh sách trống mà bên lưới vẫn thấy ca chờ: bạn đang đứng khác cơ sở hoặc khác tuần với lưới.",
              "Không duyệt được vì ca trùng giờ với một ca đã nhận của cùng người: hai ca chỉ một là thật — huỷ ca kia hoặc từ chối ca này.",
              "Máy báo không tự duyệt ca của chính mình: ca đứng tên bạn do quản lý trực tiếp duyệt, sửa lương ca và xác nhận đi làm. Nhận cả loạt thì ca của bạn bị bỏ qua, có ghi lý do.",
              'Mở ca đã duyệt mà không thấy nút "Xác nhận có đi làm": ca đó chưa diễn ra — chỉ xác nhận được ca của hôm nay trở về trước.',
              'Bấm "Xác nhận có đi làm" mà máy báo tháng lương đã chốt: số tháng đó đã đóng sổ, không xác nhận thêm được — hỏi kế toán.',
              "Bấm Duyệt, Huỷ ca hay sửa giờ ca đã duyệt mà máy báo tháng lương đã chốt: ca của tháng đó không đổi được nữa (nhận cả loạt thì ca đó bị bỏ qua, có ghi lý do) — cần điều chỉnh thì hỏi kế toán.",
            ],
          },
        ],
      },
      {
        key: "payroll",
        label: LETAN_SHIFT_TAB_LABEL.payroll,
        purpose: "Bảng giờ công của những ca đã được nhận trong tuần.",
        where: [
          "Bảng có các cột: ngày, ca, nhân viên, cơ sở, số giờ, đơn giá và thành tiền.",
          "Mỗi dòng là một ca đã được nhận; ô số giờ và ô đơn giá sửa ngay tại chỗ, bấm \"Lưu\" cuối dòng.",
          'Ô đơn giá để trống ghi mờ "theo Cơ chế lương"; cột thành tiền của dòng đó cũng ghi như vậy.',
          "Tuần chưa có ca nào được nhận thì bảng báo rõ là chưa có, không để trống.",
        ],
        howItWorks: [
          "Bảng chỉ đếm ca ĐÃ ĐƯỢC NHẬN của tuần và cơ sở đang xem — ca còn chờ không có mặt ở đây.",
          "Số giờ lấy từ khung giờ ghi trên chính ca đó, nên ca sửa giờ sẽ khác ca giữ giờ mặc định.",
          "Sửa đơn giá của một dòng là sửa cho đúng ca đó, không đổi các ca khác.",
          "Ô đơn giá để trống nghĩa là ca này không có đơn giá riêng và đi theo mức chung ở trang Cơ chế lương; xoá số trong ô rồi Lưu là quay về mức chung.",
          "Mỗi lần lưu đều được ghi lại ai sửa, sửa từ số nào sang số nào.",
          "Đây là bảng SOÁT trước khi chốt tuần, không phải nơi chốt tiền lương.",
        ],
        statuses: [
          { label: "Đã duyệt", meaning: "Ca đã được nhận — chỉ những ca này mới có mặt trong bảng." },
          { label: "Đã khoá", meaning: "Tuần đã chốt, danh sách không đổi nữa." },
        ],
        blocks: [
          {
            for: ["lead", "exec", "qc"],
            title: "Soát giờ công trước khi chốt tuần",
            steps: [
              "Mở thẻ này sau khi đã xử hết ca chờ của tuần.",
              "Rà từng dòng xem số giờ có khớp ca thực tế không.",
              'Dòng nào sai số giờ hay đơn giá thì sửa ngay tại chỗ rồi bấm "Lưu".',
              'Hiện khung đỏ "Vượt mức thường": đọc lý do; sai thì bấm "Thôi" và sửa lại, đúng thì bấm "Vẫn lưu".',
              "Rà xong mới quay lại chốt tuần.",
            ],
            troubles: [
              "Thiếu ca so với lưới: bảng chỉ đếm ca đã được nhận, ca còn chờ chưa có mặt ở đây.",
              "Số giờ của một dòng lệch dòng khác cùng loại ca: ca đó đã đổi giờ so với mặc định, mở lại ca xem có đúng ý không.",
              "Bảng trống: tuần này chưa ca nào được nhận, hoặc bạn đang đứng khác cơ sở.",
              "Báo tháng lương đã chốt: số của tháng đó đã đóng sổ, không sửa ở đây được nữa — hỏi kế toán.",
            ],
          },
        ],
      },
    ],
  },
};
